import { useEffect } from "react";

type Prefetcher = () => void;

type ConnectionLike = {
  saveData?: boolean;
  effectiveType?: string;
};

const shouldSkipPrefetch = (): boolean => {
  if (typeof navigator === "undefined") return false;
  const connection = (navigator as Navigator & { connection?: ConnectionLike }).connection;
  if (!connection) return false;
  if (connection.saveData) return true;
  if (connection.effectiveType === "slow-2g" || connection.effectiveType === "2g") return true;
  return false;
};

const scheduleIdle = (callback: () => void): (() => void) => {
  const w = window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    cancelIdleCallback?: (handle: number) => void;
  };
  if (typeof w.requestIdleCallback === "function") {
    const handle = w.requestIdleCallback(callback, { timeout: 2000 });
    return () => w.cancelIdleCallback?.(handle);
  }
  const handle = window.setTimeout(callback, 200);
  return () => window.clearTimeout(handle);
};

/**
 * Fire one-shot lazy-page prefetches the first time the calling component
 * renders. Use this when the next destination is predictable — e.g. on
 * `UsersPage` we know the user is highly likely to click into
 * `UpdateUserPage` / `CreateUserPage` next.
 *
 * Runs during browser idle time (so it doesn't compete with the current
 * page's hydration / render) and silently skips when the user is on
 * Save-Data or 2G. Each prefetcher is idempotent (dedup lives in
 * [src/routes/prefetch.ts](../../routes/prefetch.ts)), so calling this on
 * every navigation costs at most one network fetch per chunk per session.
 *
 * ```ts
 * usePrefetchOnMount(prefetchPage.userEdit, prefetchPage.userCreate);
 * ```
 */
export const usePrefetchOnMount = (...prefetchers: Prefetcher[]) => {
  useEffect(() => {
    if (prefetchers.length === 0) return;
    if (shouldSkipPrefetch()) return;
    const cancel = scheduleIdle(() => {
      for (const prefetch of prefetchers) prefetch();
    });
    return cancel;
    // Prefetchers are expected to be stable module-level functions; we
    // intentionally fire only once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
